import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {ApiError} from '../api';
import type {
  ChannelMenu,
  ChannelMenuCategory,
  ChannelMenuItem,
  ItemAvailability,
} from '../api/types';
import {useGuestSession} from '../session';
import {useGuestStream, useStreamEvents} from '../stream';

/**
 * The one-menu projection, and the live 86s that arrive on top of it.
 *
 * ## Sold out is a state of the menu, not an error at checkout
 *
 * Design principle #2: an item 86'd on the KDS vanishes here mid-session, and
 * this is the visible edge of the one-menu promise. It will happen often at a
 * busy venue, so it is modelled as ordinary data: `availability` is folded into
 * the item, updated in place by `menu.availability_changed`, and every screen
 * reads the same flag. Nothing anywhere gets to discover it late.
 *
 * ## What the client may and may not derive
 *
 * `price_satang` is the **effective** channel price the server will charge.
 * Channel overrides are already resolved. A client that re-derives a price from
 * anything is a client that will disagree with the bill.
 */

export type MenuStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface MenuValue {
  status: MenuStatus;
  menu: ChannelMenu | null;
  categories: ChannelMenuCategory[];
  /** Every item, flattened, by id. Q4 and Q5 look items up by id. */
  itemsById: ReadonlyMap<string, ChannelMenuItem>;
  error: ApiError | null;
  /** Force a re-read. Q3's "try again". */
  reload: () => void;
  /** Availability for one item, live. False when the item is not in the menu at all. */
  isAvailable: (itemId: string) => boolean;
  /** The live availability record, when there is one. Carries the reason and `until`. */
  availabilityOf: (itemId: string) => ItemAvailability | undefined;
}

const MenuContext = createContext<MenuValue | null>(null);

function indexItems(categories: ChannelMenuCategory[]): Map<string, ChannelMenuItem> {
  const map = new Map<string, ChannelMenuItem>();
  for (const category of categories) {
    for (const item of category.items) map.set(item.item_id, item);
  }
  return map;
}

export function MenuProvider({children}: {children: ReactNode}) {
  const {status: sessionStatus, client, session} = useGuestSession();
  const {resetNonce} = useGuestStream();

  // Which venue the projection on screen belongs to. When a guest re-scans at a
  // different restaurant the session swaps in place (no idle→active edge for the
  // fetch to key on), so without this the old venue's menu would stay up — the
  // "another shop's menu is stuck" half of the owner's 2026-08-17 report.
  const tenantId = session?.tenant_id ?? null;

  const [menu, setMenu] = useState<ChannelMenu | null>(null);
  const [status, setStatus] = useState<MenuStatus>('idle');
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);

  // Live availability, keyed by item id. Kept beside the projection rather than
  // merged into it so a menu re-read cannot silently undo an 86 that arrived
  // while the request was in flight.
  const [live, setLive] = useState<ReadonlyMap<string, ItemAvailability>>(new Map());

  const etag = useRef<string | null>(null);
  // The venue the projection, etag and 86 overlay currently belong to.
  const fetchedFor = useRef<string | null>(null);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (sessionStatus !== 'active' || !tenantId) return;
    const controller = new AbortController();

    // A different venue than the one on screen: the projection, its etag and the
    // live availability overlay all belonged to the previous tenant. Drop them
    // so a guest never reads one restaurant's menu at another's table.
    const venueChanged = fetchedFor.current !== null && fetchedFor.current !== tenantId;
    if (venueChanged) {
      etag.current = null;
      setMenu(null);
      setLive(new Map());
    }
    fetchedFor.current = tenantId;

    // A venue change blanks the menu, so it is loading whatever the old status
    // was; otherwise keep an on-screen menu up through a background refresh.
    setStatus((current) => (current === 'ready' && !venueChanged ? current : 'loading'));

    client
      .getMenuProjection({ifNoneMatch: etag.current, signal: controller.signal})
      .then((result) => {
        if (controller.signal.aborted) return;
        etag.current = result.etag ?? etag.current;
        if (result.data) setMenu(result.data);
        setStatus('ready');
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError({kind: 'parse', message: String(caught)}),
        );
        // Keep showing a menu already on screen. A guest reading it does not
        // care that a background refresh failed, and blanking it would be worse
        // than a slightly old price on a screen that says so elsewhere.
        setStatus((current) => (current === 'ready' ? 'ready' : 'error'));
      });

    return () => controller.abort();
  }, [client, sessionStatus, tenantId, nonce, resetNonce]);

  // `stream.reset` means the replay buffer moved past us: the live overlay may
  // have missed an 86, so it is dropped and the projection re-read.
  useEffect(() => {
    if (resetNonce > 0) setLive(new Map());
  }, [resetNonce]);

  useStreamEvents((event) => {
    if (event.type === 'menu.availability_changed' && event.availability) {
      const update = event.availability;
      setLive((current) => {
        const next = new Map(current);
        next.set(update.item_id, update);
        return next;
      });
      return;
    }
    // A structural menu change — an item renamed, a price changed, a category
    // added. Availability changes deliberately do not move `menu_version`.
    if (event.type === 'menu.updated') {
      etag.current = null;
      setNonce((n) => n + 1);
    }
  });

  const categories = useMemo(() => menu?.categories ?? [], [menu]);
  const itemsById = useMemo(() => indexItems(categories), [categories]);

  const availabilityOf = useCallback(
    (itemId: string): ItemAvailability | undefined =>
      live.get(itemId) ?? itemsById.get(itemId)?.availability,
    [live, itemsById],
  );

  const value = useMemo<MenuValue>(
    () => ({
      status,
      menu,
      categories,
      itemsById,
      error,
      reload,
      availabilityOf,
      isAvailable: (itemId) => availabilityOf(itemId)?.available ?? false,
    }),
    [status, menu, categories, itemsById, error, reload, availabilityOf],
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
}

export function useMenu(): MenuValue {
  const value = useContext(MenuContext);
  if (!value) throw new Error('useMenu must be used inside <MenuProvider>');
  return value;
}
